import { MarkdownRenderer } from './MarkdownRenderer';

/**
 * Content item type for feed display
 */
export interface ContentItem {
  slug: string;
  content: string;
  date: string;
}

interface ContentFeedProps {
  items: ContentItem[];
  isEmpty: boolean;
}

/**
 * ContentFeed component displays multiple content items in a feed layout
 *
 * Features:
 * - Displays full content inline (no separate detail pages)
 * - Shows publication date for each item
 * - Empty state when no items
 * - Responsive design with proper spacing
 */
export function ContentFeed({ items, isEmpty }: ContentFeedProps) {
  if (isEmpty) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-surface-1 border border-border rounded-lg p-8 text-center">
          <p className="text-muted">No content available in this section yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="space-y-12">
        {items.map((item) => (
          <article
            key={item.slug}
            className="border-b border-border pb-8 last:border-b-0"
          >
            {/* Full content rendered inline */}
            <div className="prose prose-lg max-w-none">
              <MarkdownRenderer content={item.content} />
            </div>

            {/* Publication date footer */}
            <footer className="mt-4 pt-4 border-t border-border">
              <time
                dateTime={item.date}
                className="text-sm text-muted"
              >
                Published {new Date(item.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  timeZone: 'UTC',
                })}
              </time>
            </footer>
          </article>
        ))}
      </div>
    </div>
  );
}
